import { Component, ViewChild, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { UserService } from '@delfi-gui/components';
import { switchMap, take } from 'rxjs';
import { AppState } from '../../../core/store';
import { AuthActions } from '../../../core/store/auth/auth.actions';
import { selectSelectedAccount } from '../../../core/store/auth/auth.selectors';
import { extractAccountContracts, mapContextToUserAccount } from '../../../core/models/user.model';
import { Dropdown } from '../dropdown/dropdown';

interface AccountItem {
  name: string;
  id: string;
}

@Component({
  selector: 'app-account-selector',
  standalone: true,
  imports: [Dropdown],
  host: { '[class.is-visible]': 'isVisible()' },
  templateUrl: './account-selector.html',
  styleUrl: './account-selector.scss',
})
export class AccountSelector {
  @ViewChild(Dropdown) private readonly dropdown?: Dropdown;

  private readonly store = inject(Store<AppState>);
  private readonly userService = inject(UserService);

  selectedAccount = toSignal(this.store.select(selectSelectedAccount), { initialValue: null });

  accounts = signal<AccountItem[]>([]);
  isLoading = signal(false);
  loadError = signal(false);
  isSaving = signal(false);
  saveError = signal(false);

  readonly getAccountId = (item: unknown): string => (item as AccountItem).id;
  readonly toAccountItem = (item: unknown): AccountItem => item as AccountItem;

  private readonly accountsLoaded = signal(false);
  isDisabled = computed(() => {
    if (!this.accountsLoaded() || this.accounts().length !== 1) return false;
    return this.selectedAccount()?.billingCrmAccountId === this.accounts()[0].id;
  });
  // Show only after load reveals at least one account, or on load error.
  // Hidden initially and during loading — avoids the flash of appearing then disappearing
  // for users who have no accounts.
  isVisible = computed(() => this.loadError() || (this.accountsLoaded() && this.accounts().length > 0));

  constructor() {
    effect(() => {
      if (this.selectedAccount() && !this.accountsLoaded() && !this.isLoading()) {
        this.loadAccounts();
      }
    });
  }

  onDropdownOpened(): void {
    this.saveError.set(false);
    if (!this.accountsLoaded() && !this.isLoading()) {
      this.loadAccounts();
    }
  }

  selectAccount(item: unknown): void {
    const account = item as AccountItem;
    this.saveError.set(false);
    this.isSaving.set(true);

    this.userService.setContext({ billingCrmAccountId: account.id }).pipe(
      take(1),
      switchMap(() => this.userService.getContext().pipe(take(1))),
    ).subscribe({
      next: (ctx) => {
        this.store.dispatch(AuthActions.userContextChanged({ account: mapContextToUserAccount(ctx) }));
        this.isSaving.set(false);
        this.dropdown?.close();
      },
      error: (err) => {
        console.error('[AccountSelector] Failed to set/refresh context after switch:', err);
        this.saveError.set(true);
        this.isSaving.set(false);
      },
    });
  }

  private loadAccounts(): void {
    this.isLoading.set(true);
    this.loadError.set(false);
    this.userService.getAccounts().pipe(take(1)).subscribe({
      next: (data) => {
        this.accounts.set(
          data.accounts.map((a) => ({ name: a.name, id: a.billingCrmAccountId })),
        );
        const contractsByAccount = Object.fromEntries(
          data.accounts.map(acct => [acct.billingCrmAccountId, extractAccountContracts(acct)]),
        );
        this.store.dispatch(AuthActions.accountContractsLoaded({ contractsByAccount }));
        this.isLoading.set(false);
        this.accountsLoaded.set(true);
      },
      error: (err) => {
        console.error('[AccountSelector] Failed to load accounts:', err);
        this.loadError.set(true);
        this.isLoading.set(false);
      },
    });
  }
}
